import { api, ApiError } from './api';
import { offlineStorage } from './offlineStorage';

type SyncStatus = 'online' | 'offline' | 'syncing';

type StatusListener = (status: SyncStatus) => void;
type UserIdProvider = () => string | null | undefined;

const MAX_RECOVERABLE_STATUS = 500;

class SyncManager {
  private status: SyncStatus = typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline';
  private listeners: Set<StatusListener> = new Set();
  private syncing = false;
  private userIdProvider: UserIdProvider = () => null;

  constructor() {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  setUserIdProvider(provider: UserIdProvider) {
    this.userIdProvider = provider;
  }

  private handleOnline = () => {
    this.setStatus('online');
    void this.processQueue();
  };

  private handleOffline = () => {
    this.setStatus('offline');
  };

  private setStatus(status: SyncStatus) {
    this.status = status;
    this.listeners.forEach((fn) => fn(status));
  }

  subscribe(fn: StatusListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  isOnline(): boolean {
    return this.status === 'online';
  }

  async processQueue(): Promise<void> {
    if (this.syncing || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    const userId = this.userIdProvider();
    if (!userId) return;

    this.syncing = true;
    this.setStatus('syncing');

    try {
      const pending = await offlineStorage.getPendingEntries(userId);

      for (const entry of pending) {
        try {
          if (entry.deleted && entry.serverId) {
            await api.delete(`/claims/${entry.claimId}/${entry.type}/${entry.serverId}`);
            if (entry.id) await import('./offlineDb').then(({ db }) => db.drafts.delete(entry.id!));
          } else if (!entry.deleted) {
            const payload = { ...entry.data, clientLocalId: entry.localId };
            if (entry.serverId) {
              await api.put(`/claims/${entry.claimId}/${entry.type}/${entry.serverId}`, payload);
              await offlineStorage.markSynced(entry.localId, entry.serverId);
            } else {
              const result = (await api.post(`/claims/${entry.claimId}/${entry.type}`, payload)) as { entry: { id: string } };
              await offlineStorage.markSynced(entry.localId, result.entry.id);
            }
          }
        } catch (err) {
          const recoverable = !(err instanceof ApiError) || err.status >= MAX_RECOVERABLE_STATUS || err.status === 409;
          await offlineStorage.markError(entry.localId, recoverable || (err instanceof ApiError && err.status >= 500));
          if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 409) {
            await offlineStorage.markError(entry.localId, false);
          }
        }
      }
    } finally {
      this.syncing = false;
      this.setStatus(typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline');
    }
  }

  destroy() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }
}

export const syncManager = new SyncManager();
